import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import {
  getSession,
  isApiError,
  signIn as apiSignIn,
  signOut as apiSignOut,
} from "../lib/api.js";
import type { SessionUser } from "../lib/api.js";

// ── Types ──────────────────────────────────────────────────────────────────

type AuthState =
  | { status: "loading" }
  | { status: "unauthenticated" }
  | { status: "active"; user: SessionUser }
  | { status: "pending"; user: SessionUser };

type AuthContextValue = {
  auth: AuthState;
  signIn: (provider: "github" | "discord" | "linkedin") => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
};

// ── Context ────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });
  const hadSession = useRef(false);
  // Prevents infinite retry — allows one automatic retry on transient server errors
  const retrying = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const { user } = await getSession();
      retrying.current = false;
      hadSession.current = true;
      setAuth({ status: user.status, user });
    } catch (err) {
      if (isApiError(err)) {
        if (err.status === 401) {
          if (hadSession.current) {
            toast.error("Your session has ended. Please sign in again.", {
              duration: Infinity,
            });
          }
          hadSession.current = false;
          retrying.current = false;
          setAuth({ status: "unauthenticated" });
          return;
        }
        // Non-401 (5xx, 429, 502): server-side error, not an auth failure.
        if (hadSession.current) {
          // Mid-session: keep existing auth state — session likely still valid.
          return;
        }
        // Initial load: retry once after 2s to recover from a brief server restart.
        if (!retrying.current) {
          retrying.current = true;
          setTimeout(() => void refresh(), 2000);
          return;
        }
        // Second attempt also failed — show login as fallback.
        retrying.current = false;
        setAuth({ status: "unauthenticated" });
      }
      // Network errors (no HTTP response): keep existing state.
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (provider: "github" | "discord" | "linkedin") => {
      await apiSignIn(provider);
      // Page will navigate away — no state update needed
    },
    [],
  );

  const signOut = useCallback(async () => {
    try {
      await apiSignOut();
    } finally {
      hadSession.current = false;
      setAuth({ status: "unauthenticated" });
    }
  }, []);

  return (
    <AuthContext.Provider value={{ auth, signIn, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

// ── Guard components ───────────────────────────────────────────────────────

export function RequireActive({
  children,
  fallback,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { auth } = useAuth();
  if (auth.status !== "active") return fallback ?? null;
  return <>{children}</>;
}

export function RequireAdmin({
  children,
  fallback,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { auth } = useAuth();
  if (auth.status !== "active" || auth.user.role !== "admin")
    return fallback ?? null;
  return <>{children}</>;
}
